import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('⚠️ Supabase credentials not found in .env file. Order submission will not work.');
}

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '');

// Helper function to sync order to Google Sheets via edge function
const syncOrderToGoogleSheets = async (order) => {
  try {
    const response = await fetch(
      `${supabaseUrl}/functions/v1/sync-order-to-sheets`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ order }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Google Sheets sync failed:', errorData);
      return false;
    }

    console.log('Order synced to Google Sheets successfully');
    return true;
  } catch (error) {
    console.error('Failed to sync to Google Sheets:', error);
    return false; // Don't fail the order if sheets sync fails
  }
};

// Helper function to submit an order
export const submitOrder = async (orderData) => {
  try {
    console.log('Attempting to submit order:', orderData);
    
    const { data, error } = await supabase
      .from('orders')
      .insert([{
        wallet_address: orderData.wallet_address,
        product_name: orderData.product_name,
        product_id: orderData.product_id,
        sol_amount: orderData.sol_amount,
        transaction_signature: orderData.transaction_signature,
        customer_name: orderData.customer_name,
        email: orderData.email,
        phone: orderData.phone || null,
        address_line1: orderData.address_line1,
        address_line2: orderData.address_line2 || null,
        city: orderData.city,
        state_province: orderData.state_province,
        postal_code: orderData.postal_code,
        country: orderData.country,
        order_status: 'pending',
        shipping_cost: orderData.shipping_cost || 0,
        tax_amount: orderData.tax_amount || 0,
        total_usd: orderData.total_usd || orderData.sol_amount
        // order_number auto-generates via trigger
      }])
      .select(); // Returns the created order with order_number

    if (error) {
      console.error('Supabase error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw error;
    }

    console.log('Order submitted successfully:', data);
    
    // Sync to Google Sheets (fire and forget - don't block on failure)
    syncOrderToGoogleSheets(data[0]).catch(err => {
      console.error('Background Google Sheets sync error:', err);
    });

    return { success: true, data: data[0] }; // Return first order with auto-generated order_number
  } catch (error) {
    console.error('Failed to submit order - Full error:', error);
    return { success: false, error: error.message };
  }
};

// Helper function to check if wallet has already ordered
export const checkExistingOrder = async (walletAddress, productId) => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('id')
      .eq('wallet_address', walletAddress)
      .eq('product_id', productId)
      .limit(1);

    if (error) {
      console.error('Error checking existing order:', error);
      return { exists: false };
    }

    return { exists: data && data.length > 0 };
  } catch (error) {
    console.error('Failed to check existing order:', error);
    return { exists: false };
  }
};
